import React, { ChangeEvent, ReactNode } from 'react';
import { Form } from 'react-bootstrap';
import styled from 'styled-components';
import DatePicker from '@mindmemobile/mm-date-picker';
import { theme } from '../theme';
const { border, success, text } = theme.palette;
interface IStyleProps {
  labelPadding?: string;
  bgSize?: string;
  heightWidth?: string;
  chbxBgColor?: string;
}

const InputWrapper = styled.div<IStyleProps>`
  /* Customized common checkbox styles that are going to be used all over the project */
  .styled-chkbox {
    cursor: pointer;
    /* display: flex; */
    .form-check-input {
      margin-right: 10px;
      height: 20px;
      width: 20px;
      background-size: 20px;
      margin-top: 2px;
      border-color: ${border.darker};
      cursor: pointer;
      &:focus {
        box-shadow: none;
      }
      &:active {
        filter: brightness(100%);
      }
      &:checked {
        border-color: ${props => props.chbxBgColor || success.dark};
        background-color: ${props => props.chbxBgColor || success.dark};
      }
    }
    label {
      cursor: pointer;
      color: ${text.main};
    }
  }
  .styled-input {
    border: 1px solid ${border.darker};
    border-radius: 4px;
    padding: 7px;
    color: ${text.main};

    &:focus-visible {
      outline: none;
    }
    ::placeholder {
      color: ${text.main};
    }
  }
  .styled-radio {
    .form-check-input {
      height: ${props => props.heightWidth || '15px'};
      width: ${props => props.heightWidth || '15px'};
      cursor: pointer;
      border: 1px solid #aaa;

      &:checked {
        background-color: #fff;
        border: 1px solid #aaa;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3E%3Ccircle r='2' fill='%2357AC2D' /%3E%3C/svg%3E%0A");
        background-position: 50% 50%;
        background-size: ${props => props.bgSize || '20px'};
      }
      &:focus,
      &:hover,
      &:active {
        box-shadow: none;
        background-color: #fff;
      }
    }
    label {
      padding-left: ${props => props.labelPadding || '10px'};
    }
  }
  input[type='number']::-webkit-inner-spin-button,
  input[type='number']::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

export interface InputProps {
  // ref?: React.Ref<HTMLInputElement>; // Added `ref`
  type: 'text' | 'textarea' | 'number' | 'email' | 'password' | 'checkbox' | 'radio' | 'date';
  id?: string;
  label?: ReactNode | string;
  value?: any;
  name?: string;
  placeholder?: string | undefined;
  error?: boolean;
  errorMessage?: string;
  disabled?: boolean;
  onChange: (e: ChangeEvent<any>, isBlur?: boolean) => void;
  className?: string;
  minDate?: Date;
  maxLength?: number;
  onDateChange?: (date: Date) => void;
  onDateChangeWithNull?: (date: Date | null) => void;
  stopClickEvent?: boolean;
  shouldEmptyDate?: boolean;
  chbxBgColor?: string;
  borderColor?: string;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      id,
      type,
      label,
      value,
      name,
      placeholder,
      error,
      disabled,
      errorMessage,
      onChange,
      className,
      minDate,
      onDateChange,
      onDateChangeWithNull,
      maxLength,
      stopClickEvent = false,
      shouldEmptyDate = false,
      chbxBgColor,
      borderColor,
      onKeyDown,
    },
    ref
  ) => {
    if (type === 'radio') {
      return (
        <>
          <InputWrapper className="chkbox-wrapper">
            <Form>
              <Form.Check
                label={label}
                type="radio"
                id={id}
                checked={value}
                bsPrefix={`styled-radio ${className}`}
                onChange={onChange}
                disabled={disabled}
                onBlur={e => onChange(e, true)}
              />
            </Form>
          </InputWrapper>
          {/* <div className="form-check styled-radio">
                    <label className="form-check-label">
                        <input type="radio" className="form-check-input" name={id} />
                        {label}
                    </label>
                </div> */}
        </>
      );
    }
    if (type === 'checkbox') {
      return (
        <InputWrapper className="chkbox-wrapper" chbxBgColor={chbxBgColor}>
          <Form>
            <Form.Check
              label={label}
              name="group1"
              type="checkbox"
              id={id}
              checked={value}
              bsPrefix={`styled-chkbox ${className}`}
              onChange={onChange}
              disabled={disabled}
              onBlur={e => onChange(e, true)}
            />
          </Form>
          {error && errorMessage && <p className="error-message">{errorMessage}</p>}
        </InputWrapper>
      );
    }
    if (type === 'textarea') {
      return (
        <InputWrapper className="textarea-wrapper">
          <textarea
            style={{ borderColor: borderColor }}
            id={id}
            value={value}
            name={name}
            className={`${className || 'styled-input'} ${error ? 'error-border' : ''}`}
            placeholder={placeholder}
            onChange={onChange}
            disabled={disabled}
            onBlur={e => onChange(e, true)}
          ></textarea>
        </InputWrapper>
      );
    }

    if (type == 'date') {
      return (
        <InputWrapper>
          <DatePicker
            type="text"
            minDate={minDate ? minDate.toISOString() : ''}
            className={`${className} ${error ? 'error-border' : ''}`}
            onChange={(isoDate: string | null, formattedDate: string | null) => {
              const newDate = isoDate ? new Date(isoDate) : shouldEmptyDate ? null : new Date();
              if (shouldEmptyDate) {
                onDateChangeWithNull && onDateChangeWithNull(newDate);
              } else {
                onDateChange && onDateChange(newDate!);
              }
            }}
            value={value || null}
          />
        </InputWrapper>
      );
    }

    return (
      <>
        <InputWrapper className="input-wrapper">
          <input
            ref={ref}
            type={type}
            id={id}
            value={value}
            checked={value}
            name={name}
            style={{ borderColor: borderColor }}
            className={`${className || 'styled-input'} ${error ? 'error-border' : ''}`}
            placeholder={placeholder}
            onChange={onChange}
            onBlur={e => onChange(e, true)}
            disabled={disabled}
            maxLength={maxLength}
            onClick={e => stopClickEvent && e.stopPropagation()}
            onKeyDown={onKeyDown}
            // content={content}
          />
          {label && <label htmlFor={id}>{label}</label>}
          {error && errorMessage && <p className="error-message">{errorMessage}</p>}
        </InputWrapper>
      </>
    );
  }
);

export default Input;
